/**
 * Convenience hook that manages the open/close/pending state for `DeleteDialog`.
 *
 * Eliminates the boilerplate of maintaining `pendingItem`, `dialogRef`,
 * `openDelete`, and `closeDelete` in every list page.
 *
 * @example
 * ```tsx
 * const {
 *   dialogRef,
 *   pendingItem,
 *   openDelete,
 *   closeDelete,
 * } = useDeleteDialog<ILocationHolidayRow>();
 *
 * // In table actions:
 * onClick: (row) => openDelete(row)
 *
 * // In JSX:
 * <DeleteDialog
 *   ref={dialogRef}
 *   title="Delete holiday?"
 *   entityLabel={pendingItem?.locationHoliday.holidayName}
 *   onConfirm={async () => { await service.deleteById(pendingItem!.id); }}
 *   onCancel={closeDelete}
 * />
 * ```
 */
import { useCallback, useRef, useState } from "react";

import type { DeleteDialogHandle } from "./DeleteDialog";

export type UseDeleteDialogReturn<T> = {
  /** Attach to `<DeleteDialog ref={dialogRef} />` */
  dialogRef: React.RefObject<DeleteDialogHandle | null>;
  /** The row / entity staged for deletion, or `null` when the dialog is closed. */
  pendingItem: T | null;
  /** Stage an item and open the dialog. */
  openDelete: (item: T) => void;
  /** Clear the staged item and close the dialog. */
  closeDelete: () => void;
};

/**
 * Manages open/close state and the staged item for a `DeleteDialog`.
 * Generic over the row type used in the calling list page.
 */
export function useDeleteDialog<T>(): UseDeleteDialogReturn<T> {
  const dialogRef = useRef<DeleteDialogHandle | null>(null);
  /** WHY: Prefer `number` here — avoids NodeJS `Timer`/`Timeout` vs DOM `number` mismatch in toolchain typings. */
  const showModalTimerRef = useRef<number | null>(null);
  const [pendingItem, setPendingItem] = useState<T | null>(null);

  const openDelete = useCallback((item: T) => {
    if (showModalTimerRef.current !== null) {
      clearTimeout(showModalTimerRef.current);
      showModalTimerRef.current = null;
    }
    setPendingItem(item);
    // WHY: `showModal()` must run after React commits `pendingItem` so the dialog body/title matches
    //      the staged action (activate vs retire vs delete). Opening in the same tick left `pendingItem`
    //      null on first paint, which showed the delete copy for every action.
    //      Deferring to the next task also avoids the opener click's trailing events hitting the modal
    //      backdrop and closing instantly (CordioFHIR row actions share this hook).
    showModalTimerRef.current = window.setTimeout(() => {
      showModalTimerRef.current = null;
      dialogRef.current?.showModal();
    }, 0);
  }, []);

  const closeDelete = useCallback(() => {
    if (showModalTimerRef.current !== null) {
      clearTimeout(showModalTimerRef.current);
      showModalTimerRef.current = null;
    }
    dialogRef.current?.close();
    setPendingItem(null);
  }, []);

  return { dialogRef, pendingItem, openDelete, closeDelete };
}
